// Firebase initialisation — single app instance shared across the admin UI.
// Emulator wiring is env-driven so `npm run dev` works before any cloud
// resources exist (P0 definition of done).
import { getApps, initializeApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'mqr-whatsapp',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
// Cloud Functions region — matches functions/src/index.ts.
export const functions = getFunctions(app, 'us-central1');
// Cloud Storage — marketing-site media (logo, tutorial videos) under /site/**.
export const storage = getStorage(app);
export const googleProvider = new GoogleAuthProvider();

// Base URL of the public REST API (apiV1 onRequest). Shown in Settings so users
// can point Zapier / Make / n8n / custom code at it.
const projectId = app.options.projectId || 'mqr-whatsapp';
export const apiBaseUrl = import.meta.env.VITE_USE_EMULATORS === 'true'
  ? `http://127.0.0.1:5001/${projectId}/us-central1/apiV1`
  : `https://us-central1-${projectId}.cloudfunctions.net/apiV1`;

// Amwal → us payment callback. Paste into the Amwal dashboard as the webhook URL.
export const billingWebhookUrl = import.meta.env.VITE_USE_EMULATORS === 'true'
  ? `http://127.0.0.1:5001/${projectId}/us-central1/billingWebhook`
  : `https://us-central1-${projectId}.cloudfunctions.net/billingWebhook`;

// Public form definition + submission endpoint (used by the public fill page).
export const publicFormUrl = import.meta.env.VITE_USE_EMULATORS === 'true'
  ? `http://127.0.0.1:5001/${projectId}/us-central1/publicForm`
  : `https://us-central1-${projectId}.cloudfunctions.net/publicForm`;

// Tracked click-to-WhatsApp redirect (used in ad campaigns).
export const adClickUrl = import.meta.env.VITE_USE_EMULATORS === 'true'
  ? `http://127.0.0.1:5001/${projectId}/us-central1/adClick`
  : `https://us-central1-${projectId}.cloudfunctions.net/adClick`;

// Country → currency for pricing. Public, cheap, and cached per visitor.
export const geoResolveUrl = import.meta.env.VITE_USE_EMULATORS === 'true'
  ? `http://127.0.0.1:5001/${projectId}/us-central1/geoResolve`
  : `https://us-central1-${projectId}.cloudfunctions.net/geoResolve`;

// Public marketing-site content (operator CMS).
export const siteContentUrl = import.meta.env.VITE_USE_EMULATORS === 'true'
  ? `http://127.0.0.1:5001/${projectId}/us-central1/siteContentGet`
  : `https://us-central1-${projectId}.cloudfunctions.net/siteContentGet`;

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

export default app;
